import { useNavigate } from '@tanstack/react-router';
import { useAppTheme } from 'hooks/useAppTheme';
import { useState } from 'react';
import { formatCurrency } from 'utils/currencies';

import imgEllipsis from 'components/AdvancedTable/assets/icon-ellipsis.svg';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import { GainLoss } from 'components/GainLoss/GainLoss';
import { PortfolioSummaryEntry } from '../../types';

import { useContent } from '@altify/utils';
import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';
import Dropdown from 'components/Dropdown/Dropdown';
import { InfoButton } from 'components/InfoButton/InfoButton';
import Modal from 'components/Modal/Modal';
import { TimeFrameKey, timeFrames } from 'data/time-frames';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import useAppLanguage from 'hooks/useAppLanguage';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import appUrls from 'utils/app-urls';
import { gainLossContent } from '../../gainLoss.content';
import messages from '../../messages';
import { Button, RollingAmount } from '@altify/components';
import { ActiveProductDtoTypeEnum } from 'services/generated';

const cdnUrl = appUrls.getCDNUrl();

const imgDeposit = `${import.meta.env.VITE_ALTIFY_CDN_URL}/assets/explore/arrow-down-white.svg`;
const imgWithdraw = `${import.meta.env.VITE_ALTIFY_CDN_URL}/assets/explore/arrow-up-white.svg`;
const imgCashBalance = `${cdnUrl}/assets/portfolio/icon-cash-balance.svg`;
const imgHoldings = `${cdnUrl}/assets/portfolio/icon-holdings.svg`;
const imgReserved = `${cdnUrl}/assets/portfolio/icon-reserved-holdings.svg`;
const imgReservedModal = `${cdnUrl}/assets/portfolio/icon-reserved-modal.svg`;

const PortfolioSummary = ({
  timeFrame,
  showGains,
  onChangeTimeFrame,
}: {
  timeFrame: TimeFrameKey;
  showGains: boolean;
  onChangeTimeFrame: (timeFrame: TimeFrameKey) => void;
}) => {
  const { products, isLoading: isLoadingProducts } =
    useProductsWithHoldingsAndRates();
  const { functionalCurrency, isLoading: isLoadingFunctionalCurrency } =
    useFunctionalCurrency();
  const { gainsAndLosses, isLoading: isLoadingPortfolioGainsValue } =
    usePortfolioGainsAndLosses(timeFrame);

  const { data: profileData, isLoading: isLoadingProfile } =
    useGetProfileQuery();
  const isLoading =
    isLoadingProducts ||
    isLoadingFunctionalCurrency ||
    isLoadingPortfolioGainsValue ||
    isLoadingProfile;

  const localizedGainLossContent = useContent(gainLossContent);

  const isVerified = profileData?.verificationStatus == 'Verified';

  const withdrawablAssets =  products?.filter(_ => _.availableAmount > 0 && _.isWithdrawable);
  const canDeposit = isVerified;
  const canWithdraw = isVerified && (withdrawablAssets && withdrawablAssets?.length > 0);

  const totalPortfolioValue = products?.reduce(
    (acc, curr) => acc + (curr.totalAmountInDisplay ?? 0),
    0,
  );

  const reservedProducts = products?.filter(p => p.reservedAmount > 0);
  const totalReservedPortfolioValue = reservedProducts?.reduce(
    (acc, curr) => acc + (curr.reservedAmountInDisplay ?? 0),
    0,
  );

  const totalPortfolioFiatValue = products
    ?.filter(p => p.type === ActiveProductDtoTypeEnum.Fiat || p.type === ActiveProductDtoTypeEnum.Stablecoin)
    .reduce((acc, curr) => acc + (curr.totalAmountInDisplay ?? 0), 0);

  const totalNonFiatPortfolioValue =
    totalPortfolioValue != undefined && totalPortfolioFiatValue != undefined
      ? totalPortfolioValue - totalPortfolioFiatValue
      : undefined;

  const navigate = useNavigate();
  const { theme } = useAppTheme();
  const language = useAppLanguage();

  const viewMessages = messages[language].portfolioSummary;

  const [showReservedModal, setShowReservedModal] = useState<boolean>(false);

  const handleFunds = (type: 'DEPOSIT' | 'WITHDRAW') => {
    navigate({ to: `/funds/${type === 'DEPOSIT' ? 'deposit' : 'withdraw'}` });
  };

  const items: PortfolioSummaryEntry[] = [
    {
      icon: imgCashBalance,
      text: viewMessages.cashBalance,
      rightComponent: (
        <RollingAmount
          amount={formatCurrency(
            totalPortfolioFiatValue,
            functionalCurrency?.code!,
          )}
          isPortfolioAmount={false}
        />
      ),
    },
    {
      icon: imgHoldings,
      text: 'Holdings',
      rightComponent: !showGains ? (
        <div className="text-price-green h-8 mt-1">-</div>
      ) : (
        <div className="text-right">
          <RollingAmount
            amount={formatCurrency(
              totalNonFiatPortfolioValue,
              functionalCurrency?.code as string,
            )}
            isPortfolioAmount={false}
            className="justify-end"
          />
          <div className="flex flex-row items-center">
            <GainLoss
              arrow
              value={isLoading ? null : gainsAndLosses?.gainAmount}
              symbol={functionalCurrency?.symbol as string}
              background={true}
            />
            <InfoButton
              className='ml-1'
              title={localizedGainLossContent.infoTitle}
              text={localizedGainLossContent.infoText}
            />
          </div>
        </div>
      ),
    },
    {
      icon: imgReserved,
      text: viewMessages.reservedHoldings,
      rightComponent: (
        <div className="flex flex-row align-center items-center justify-items-center">
          <RollingAmount
            amount={formatCurrency(
              totalReservedPortfolioValue,
              functionalCurrency?.code!,
            )}
            isPortfolioAmount={false}
          />
          {totalReservedPortfolioValue && totalNonFiatPortfolioValue ? (
            <button
              type="button"
              onClick={() => setShowReservedModal(true)}
              className="focus:outline-none">
              <img src={imgEllipsis} alt="reserved-items-info" />
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  const ActionButtons = () => (
    <div className="flex flex-row w-full gap-4 mt-6 mb-4">
      {canDeposit &&
      <Button
        size="medium"
        className="px-2 flex-grow text-white"
        color='primary'
        onPress={() => {handleFunds('DEPOSIT')}}
        fullWidth={false}
      >
        <img src={imgDeposit} alt="Deposit" className='mr-2' />
        <div>{viewMessages.deposit}</div>
      </Button>
      }
      {canWithdraw &&
        <Button
          size="medium"
          color='secondary'
          className="px-2 flex-grow text-white"
          onPress={() => {handleFunds('WITHDRAW')}}
          fullWidth={false}
        >
          <img src={imgWithdraw} alt="withdraw" className='mr-2' />
          <div>{viewMessages.withdraw}</div>
        </Button>
      }
    </div>
  );

  return (
    <>
      {/* Desktop View */}
      <div
        // @ts-ignore
        style={{ backgroundColor: '#192037' }}
        className="flex flex-col flex-grow relative m-0 md:m-4 lg:mt-10 mt-0 md:mt-4 xl:mt-4 md-mr-1 p-4 rounded-xl">
        {isLoading && !totalPortfolioFiatValue ? (
          <div className="p-12" style={{ height: '23rem' }}>
            <CustomLoader />
          </div>
        ) : (
          <div>
            <div className="absolute right-2 top-3">
              <Dropdown
                options={Object.values(timeFrames)}
                value={timeFrame}
                onChange={onChangeTimeFrame}
              />
            </div>
            <div className="text-body-text">{viewMessages.totalHeading}</div>
            <div className="text-header-text">
              <RollingAmount
                amount={formatCurrency(
                  totalPortfolioValue,
                  functionalCurrency?.code!,
                )}
                isPortfolioAmount={true}
              />
            </div>
            {isLoading ? (
              <div className="block md:hidden w-80 h-5 mt-1">-</div>
            ) : gainsAndLosses?.gainPercentage != 0 ? (
              <div
                className="block md:hidden"
                style={{
                  color:
                    gainsAndLosses?.gainPercentage! > 0
                      ? theme.success
                      : theme.priceRed,
                }}>
                {viewMessages.subHeading(
                  gainsAndLosses?.gainAmount!,
                  gainsAndLosses?.gainPercentage!,
                  timeFrame,
                  functionalCurrency?.code!,
                )}
              </div>
            ) : null}
            <div className="flex flex-col w-full text-header-text mt-2 mb-2">
              {items.map((item: PortfolioSummaryEntry, i: number) => (
                <div
                  key={item.text}
                  className="flex flex-row w-full justify-center items-center">
                  <img src={item.icon} alt={item.text} className="h-10" />
                  <div
                    className="flex flex-row justify-between w-full items-center px-4 py-4"
                    style={{
                      borderBottom:
                        i < items?.length - 1 ? `1px solid #2F3A5C` : 'none',
                    }}>
                    <div>{item.text}</div>
                    <div>{item.rightComponent}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="block">
              <ActionButtons />
            </div>
          </div>
        )}
      </div>


      <Modal
        show={showReservedModal}
        closeModal={() => setShowReservedModal(false)}
        hideModal={() => setShowReservedModal(false)}
        content={
          <div className="items-center text-center flex-col">
            <img
              src={imgReservedModal}
              className="w-36 mx-auto"
              alt="reserved-amounts"
            />
            <div className="mt-4 mb-1 text-xl text-header-text">
              {viewMessages.reservedAssets}
            </div>
            <div className="mb-8 text-md text-body-text">
              {viewMessages.assetsPendingWithdrawal}
            </div>
            {reservedProducts?.map((p, i: number) => {
              return (
                <div key={i} className="w-full mt-4 mb-4">
                  <div
                    className="flex flex-row justify-between pb-2"
                    style={{
                      borderBottom: `1px solid ${theme.inputBackground}`,
                    }}>
                    <div className="flex flex-row">
                      <div className="mr-4">
                        <CurrencyIcon currencyCode={p.code} isLockedAsset={p.type == ActiveProductDtoTypeEnum.Locked} />
                      </div>
                      <div className="text-left">
                        <div className="text-header-text">{p?.code}</div>
                        <div>{p.name}</div>
                      </div>
                    </div>
                    <div className="flex flex-row">
                      <div className="align-right text-right">
                        <div>{viewMessages.amount}</div>
                        <div className="text-header-text">
                          {formatCurrency(p?.reservedAmount, p?.code)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="focus:outline-none"
                        onClick={() =>
                          navigate({
                            to: '/funds/withdraw',
                            search: { pendingModal: p.code },
                          })
                        }>
                        <img src={imgEllipsis} alt="options" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        }
      />
    </>
  );
};

export default PortfolioSummary;
